<script setup>
import { railsApi } from '@/utils/railsApi'

definePage({
  meta: {
    adminArea: true,
  },
})

const { t, te } = useI18n()

const loading = ref(false)
const error = ref('')
const trainers = ref([])
const q = ref('')
const lifecycleState = ref('')

const lifecycleOptions = computed(() => [
  { title: t('admin.trainers.lifecycleAny'), value: '' },
  { title: t('admin.lifecycle.onboarding'), value: 'onboarding' },
  { title: t('admin.lifecycle.active'), value: 'active' },
  { title: t('admin.lifecycle.suspended'), value: 'suspended' },
  { title: t('admin.lifecycle.deactivated'), value: 'deactivated' },
])

const statusColor = state => {
  switch (state) {
  case 'active': return 'success'
  case 'suspended': return 'warning'
  case 'deactivated': return 'secondary'
  case 'onboarding': return 'info'
  default: return 'default'
  }
}

const lifecycleLabel = state => {
  const key = `admin.lifecycle.${state}`

  return te(key) ? t(key) : state
}

const load = async () => {
  loading.value = true
  error.value = ''
  try {
    const params = new URLSearchParams()
    if (q.value.trim())
      params.set('q', q.value.trim())
    if (lifecycleState.value)
      params.set('lifecycle_state', lifecycleState.value)

    const query = params.toString()
    const data = await railsApi(`/api/v1/admin/trainers${query ? `?${query}` : ''}`)

    trainers.value = data.trainers || []
  }
  catch (err) {
    error.value = err.payload?.error || err.message || t('admin.trainers.loadError')
  }
  finally {
    loading.value = false
  }
}

onMounted(load)

const onSearch = () => {
  load()
}
</script>

<template>
  <div>
    <div class="d-flex flex-wrap align-center justify-space-between gap-4 mb-4">
      <h1 class="text-h4 mb-0">
        {{ $t('admin.trainers.title') }}
      </h1>
    </div>

    <VCard class="mb-4">
      <VCardText>
        <VForm @submit.prevent="onSearch">
          <VRow>
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="q"
                :label="$t('admin.trainers.search')"
                :placeholder="$t('admin.trainers.searchPlaceholder')"
                clearable
              />
            </VCol>
            <VCol
              cols="12"
              md="4"
            >
              <VSelect
                v-model="lifecycleState"
                :items="lifecycleOptions"
                item-title="title"
                item-value="value"
                :label="$t('admin.trainers.lifecycle')"
              />
            </VCol>
            <VCol
              cols="12"
              md="2"
              class="d-flex align-end"
            >
              <VBtn
                type="submit"
                color="primary"
                block
                :loading="loading"
              >
                {{ $t('admin.trainers.search') }}
              </VBtn>
            </VCol>
          </VRow>
        </VForm>
      </VCardText>
    </VCard>

    <VAlert
      v-if="error"
      type="error"
      variant="tonal"
      class="mb-4"
      role="alert"
    >
      {{ error }}
    </VAlert>

    <VCard>
      <VCardText>
        <p
          v-if="!loading && !trainers.length"
          class="text-medium-emphasis mb-0"
        >
          {{ $t('admin.trainers.empty') }}
        </p>
        <VTable v-else>
          <thead>
            <tr>
              <th scope="col">
                {{ $t('admin.trainers.columns.email') }}
              </th>
              <th scope="col">
                {{ $t('admin.trainers.columns.name') }}
              </th>
              <th scope="col">
                {{ $t('admin.trainers.columns.business') }}
              </th>
              <th scope="col">
                {{ $t('admin.trainers.columns.lifecycle') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="trainer in trainers"
              :key="trainer.id"
            >
              <td>
                <RouterLink :to="{ name: 'admin-trainers-id', params: { id: trainer.id } }">
                  {{ trainer.email }}
                </RouterLink>
              </td>
              <td>{{ trainer.name || '—' }}</td>
              <td>{{ trainer.business_name || '—' }}</td>
              <td>
                <VChip
                  size="small"
                  :color="statusColor(trainer.lifecycle_state)"
                  variant="tonal"
                >
                  {{ lifecycleLabel(trainer.lifecycle_state) }}
                </VChip>
              </td>
            </tr>
          </tbody>
        </VTable>
      </VCardText>
    </VCard>
  </div>
</template>
